/* ═══════════════════════════════════════════════════════════════════════════
   ManDana — a record says where it is, honestly
   ---------------------------------------------------------------------------
   One dot, five sentences, and one rule that is the whole story: A DOCUMENT
   THAT HAS SYNCED IS NOT THE SAME AS A FILE THAT HAS ARRIVED. A desk whose
   scene travelled last night, carrying the name of an image still sitting in
   the upload queue, is not "Synced" — it is a promise this device is the only
   one able to keep. Saying "Synced" there is the app telling somebody their
   work is safe while it is on exactly one disk, which is the failure this
   mark exists to make visible rather than the one it should hide.

   So the mark reads BOTH, and the loudest true thing wins:

     Waiting to upload              bytes are owed. Only here.
     This device only               the document has never been sent.
     Changed since sync             the newest words are only here.
     In the cloud, not on this…     it travelled; the bytes have not come down.
     Synced                         both halves are elsewhere too.

   THE FIRST THREE WORDS ARE THE LIBRARIAN'S, VERBATIM (md-librarian.ts:304).
   The X-ray column has said them since it shipped, and a second vocabulary for
   the same three facts is how one app comes to have two names for everything.
   The last two are new because they are about bytes, which that column has
   never spoken about.

   THE WORDS ARE A PURE FUNCTION, which is where the thinking is rather than a
   concession to a test runner — the same stance md-transfer.jsx took an hour
   ago. Five states, an ordering between them that is a judgement about risk,
   and a rule for reading many documents as one record. mdWhereSay proves
   without a DOM; the component below only arranges what it said.

   NO SPINNER, NO SHIMMER, NO TRANSITION. Reduced motion is a blanket rule in
   index.html and the atomic-panel rule forbids it besides, so a mark that
   animated would need exempting from both. There is nothing to animate: every
   one of the five is a settled fact, not a thing in progress. What IS in
   progress gets md-transfer.jsx's bar, which can count.

   INK ALPHAS, NO ACCENT. Five states told apart by weight and by fill —
   nothing here asks what colour the account is, so nothing here can be
   invisible on one ground and shouting on the other. (#C4B5FF is chosen for
   the dark ground and nearly disappears on the light one; a 7px dot has no
   room to survive that.)

   THE COMPONENT NEVER REACHES FOR A STORE. The caller hands it what it knows,
   which is what lets a desk card, a file embed and a record head each answer
   the question from the door that actually knows — and lets all three be
   rendered against a fixture. mdWhereSnapshot and mdWhereRead are the door-
   reading half, kept separate and asked ONCE by a surface drawing many marks:
   mdSyncStates walks all of storage, and paying that per card is how a desk
   list with forty desks becomes a stutter.
   ═══════════════════════════════════════════════════════════════════════════ */

/* The three the Librarian already chose, and the two about bytes it never had
   to. Written out rather than imported: md-librarian.ts is headless by
   contract and keeps its copy private, and tests/where-says.test.js asserts
   the three match its source so the two cannot drift apart in silence. */
const MD_WHERE_WORDS = {
  waiting: 'Waiting to upload',
  local: 'This device only',
  changed: 'Changed since sync',
  cloud: 'In the cloud, not on this device',
  synced: 'Synced',
};

/* How each one is drawn, and it is a judgement about RISK rather than
   decoration. "Synced" is the state a whole screen is in and has to recede —
   forty loud dots say nothing. "Waiting to upload" is the one thing on that
   screen worth finding. Hollow is the second axis, so `changed` and `cloud`
   can share a weight and still be told apart: a ring for "it is elsewhere,
   just not here", a disc for "it is here, and only here". */
const MD_WHERE_DRAW = {
  waiting: { alpha: 0.78, hollow: false },
  local: { alpha: 0.60, hollow: false },
  changed: { alpha: 0.42, hollow: false },
  cloud: { alpha: 0.42, hollow: true },
  synced: { alpha: 0.20, hollow: true },
};

/* Worst first, exactly as md-librarian.ts:304 orders them and for the reason
   it wrote down: a record's reading is the WORST of its documents. One
   unsynced paragraph is the thing worth knowing, and averaging it away against
   a synced index row hides the case the mark exists for. */
const MD_WHERE_DOC_ORDER = ['local', 'changed', 'synced'];

/** The worst of several document states, or '' when none is understood.

    A word this build has never heard costs ONE SKIPPED DOCUMENT and nothing
    else — the same stance md-librarian.ts takes, and the same one md-record.ts
    takes on an unknown ref type. Ranking it would let a newer client's state
    decide a reading it knows nothing about. */
function mdWhereWorst(doc) {
  const list = Array.isArray(doc) ? doc : [doc];
  let worst = -1;
  for (const one of list) {
    const rank = MD_WHERE_DOC_ORDER.indexOf(one);
    if (rank >= 0 && (worst < 0 || rank < worst)) worst = rank;
  }
  return worst < 0 ? '' : MD_WHERE_DOC_ORDER[worst];
}

/** Where one thing is, in a sentence — or null when nothing is known.

    Takes `{doc, pending, here}`. `doc` is one of md-sync.js's three states or
    a list of them; `pending` is true while any bytes this thing owns are still
    owed to the store; `here` is whether those bytes are on this device, and
    null when there are none or the answer has not come back yet.

    Pure: no store, no clock, no DOM.

    THE ORDER IS RISK, NOT SEVERITY THEATRE. Owed bytes first, because they
    exist in one place and a lost laptop loses them. Then a document never
    sent, then one whose newest words were not. `cloud` is last of the four
    because nothing is at risk there at all — it is an inconvenience, not a
    loss, and drawing it as loudly as the others would teach people to ignore
    all of them.

    NULL RATHER THAN A GUESS. A built-in has no document and a fixture may have
    nothing to say; a dot that means nothing is worse than no dot, because a
    reader has to learn it means nothing before they can stop reading it. */
function mdWhereSay(where) {
  const w = where && typeof where === 'object' ? where : {};
  const doc = mdWhereWorst(w.doc);
  const here = w.here === true ? true : (w.here === false ? false : null);
  const state = w.pending ? 'waiting'
    : doc === 'local' ? 'local'
      : doc === 'changed' ? 'changed'
        : here === false ? 'cloud'
          : doc === 'synced' ? 'synced' : '';
  if (!state) return null;
  return { state, label: MD_WHERE_WORDS[state], ...MD_WHERE_DRAW[state] };
}

/** Everything the doors know, read once for a surface drawing many marks.

    mdSyncStates walks all of storage. It is a price worth paying once per
    render of a desk list and never worth paying once per desk — the shape
    md-librarian.ts landed on for the same question. */
function mdWhereSnapshot() {
  const g = window;
  return {
    states: g.mdSyncStates ? g.mdSyncStates() : {},
    /* Two questions, two answers, and the difference matters. mdBlobPending
       answers HASHES; mdBlobPendingRefs answers the DOCUMENTS that own bytes
       in flight, keyed as mdLocalDocs names them. A file embed knows its hash;
       a desk knows its own name. Asking either one the other's question gets a
       set matching nothing and a mark reporting safety it never checked. */
    hashes: g.mdBlobPending ? g.mdBlobPending() : [],
    refs: g.mdBlobPendingRefs ? g.mdBlobPendingRefs() : [],
  };
}

/** Compose what mdWhereSay needs from what a surface knows about one thing.

    `keys` are storage keys (`md:boards`); `refs` are document names as
    mdLocalDocs spells them (`board:abc`); `hashes` are blob names. `here` is
    handed in rather than asked, because mdBlobHave is asynchronous and a
    render cannot wait for it.

    `doc` is the passthrough for a caller whose subject has no storage key at
    all. A file embed is the case: its bytes are named by their own hash and
    live in no document, so "has this name ever been offered to a store?" —
    mdFileTravels — is the only document-shaped fact there is, and it is the
    surface that knows it rather than mdSyncStates.

    A build without the doors answers "nothing is owed" rather than throwing —
    the same reason md-embed.jsx asks mdFileTravels at render. */
function mdWhereRead(what, snapshot) {
  const g = window;
  const w = what && typeof what === 'object' ? what : {};
  const snap = snapshot || mdWhereSnapshot();
  const doc = Array.isArray(w.doc) ? [...w.doc] : (w.doc ? [w.doc] : []);
  for (const key of (w.keys || [])) {
    if (typeof key !== 'string' || !key) continue;
    doc.push(g.mdSyncStateOf ? g.mdSyncStateOf(key, snap.states) : '');
  }
  const owed = (mine, theirs) =>
    (mine || []).some((one) => typeof one === 'string' && one && (theirs || []).indexOf(one) >= 0);
  return {
    doc,
    pending: owed(w.hashes, snap.hashes) || owed(w.refs, snap.refs),
    here: w.here === undefined ? null : w.here,
  };
}

/** The dot, rhyming with md-settings.jsx's LiveMark.

    STATES A FACT, DOES NOT CELEBRATE ONE, and is not a control: there is
    nothing to press on a fact. It carries its name for assistive technology
    because it is the only thing saying this, and the same name as a `title`
    because a dot meaning five different things says nothing at all to somebody
    who can see it. */
function MdWhereMark({ where, size = 7, style, ...rest }) {
  /* Inside render, never hoisted — a colour taken at module scope freezes the
     theme the first tab to load it happened to be in. */
  const ink = window.ink;
  const said = mdWhereSay(where);
  if (!said) return null;
  const colour = ink(said.alpha);
  return <span role="img" aria-label={said.label} title={said.label}
    data-md-where={said.state}
    style={{ display: 'inline-block', boxSizing: 'border-box', flex: `0 0 ${size}px`,
      width: size, height: size, borderRadius: size,
      background: said.hollow ? 'transparent' : colour,
      border: said.hollow ? `1.5px solid ${colour}` : 'none',
      ...style }} {...rest} />;
}

Object.assign(window, {
  MD_WHERE_WORDS, MD_WHERE_DRAW, mdWhereWorst, mdWhereSay,
  mdWhereSnapshot, mdWhereRead, MdWhereMark,
});
